import { formatISO } from "date-fns";
import { action, flow, observable } from "mobx";
import { Observer } from "mobx-react";
import { Button } from "primereact/button";
import React, { useContext, useEffect, useMemo } from "react";
import { QueryClient, useQueryClient } from "react-query";
import styled from "styled-components";
import { BackendResponse } from "../../../../api/BackendResponse";
import { candidateAPI } from "../../../../api/candidate";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { scrollIntoView } from "../../../../WSUtils/scrollIntoView";
import { redirect } from "../../../../WSUtils/urlUtils";
import {
  invalidateQueries,
  useLatestQuery,
} from "../../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { useStore2 } from "../../../../WSUtils/useStore";
import { concatClassnames } from "../../../../WSUtils/util";
import { WSFormGrid } from "../../../../WSUtils/WSFormGrid";
import { CVPSearchParams } from "../../../../WSUtils/wsQueryStringStore";
import { sentryAssert, sentryAssertData } from "../../../../WSUtils/wsSentry";
import { CVEditContext, CVEditContextStore } from "../../CVEditContext";
import { CVEditInvalidStateReminder } from "../../CVEditInvalidStateReminder";
import { CVEditCourseEditor } from "./CVEditCourseEditor";
import { CVEditCourseList } from "./CVEditCourseList";
import { CVEditCourseSegmentStore } from "./CVEditCourseSegmentStore";

export const CVEditCourseSegment = () => {
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);
  const queryClient = useQueryClient();
  const L = useLocStore(LocContext.CV);

  const courseSegmentStore = useStore2(
    "CVEditCourseSegmentStore",
    (dt) =>
      new CVEditCourseSegmentStore({
        name: "CVEditCourseSegmentStore",
        parentDt: dt,
        useHooks: (t: CVEditCourseSegmentStore) => {
          t.userInfo = useContext(UserInfoContext);
          t.queryClient = useQueryClient();
          t.cvEditContext = useContext(CVEditContext);
        },
      })
  );

  const { data: courses } = useLatestQuery(
    ["candidateAPI.getCoursesForCandidate"],
    () =>
      candidateAPI.getCoursesForCandidate({
        userInfo,
      })
  );

  sentryAssertData([courses], { userInfo });

  useEffect(
    action(() => {
      courseSegmentStore.courses = courses;
    }),
    [courses]
  );

  const saving = useMemo(() => observable.box(false), []);

  return (
    <Observer
      render={() => {
        const courseEditorStore = courseSegmentStore.courseEditorStore;

        const showPlaceholder = courseSegmentStore.nTotalListItems === 0;

        return (
          <CVEditCourseSegmentStyles className="cve-course-segment cve-segment">
            <div className="cvp-h2 cve-header">
              {L.id("5443421ff706").loc("Kurs")}
            </div>
            <CVEditCourseList courseSegmentStore={courseSegmentStore} />

            {!!courseEditorStore && (
              <>
                <div className="cve-separator" />
                <CVEditCourseEditor courseSegmentStore={courseSegmentStore} />
              </>
            )}

            <WSFormGrid
              float
              className={concatClassnames([
                "cve-button-bar",
                showPlaceholder && "cve-button-bar-placeholder",
              ])}
            >
              {!courseEditorStore && (
                <Button
                  className={concatClassnames([
                    "p-button-outlined cve-new-course-button",
                    showPlaceholder &&
                      "cve-empty-list-placeholder cve-empty-course-placeholder",
                  ])}
                  icon="pi pi-plus"
                  label={L.id("80c823c75784").loc("Nytt kurs")}
                  onClick={action((evt) => {
                    evt.preventDefault();

                    redirect(null, {
                      userInfo,
                      mergeQs: { [CVPSearchParams.cvpec]: "new" },
                    });

                    return false;
                  })}
                />
              )}

              <CVEditInvalidStateReminder
                showReminder={() => {
                  const courseEditorStore =
                    courseSegmentStore.courseEditorStore;

                  if (!courseEditorStore) return false;
                  return courseEditorStore.visibleValidationErrors.length > 0;
                }}
              />
              {!!courseEditorStore && (
                <Button
                  className="p-button-outlined cve-save-course-button"
                  label={L.id("27d860243527").loc("Lagre kurs")}
                  loading={saving.get()}
                  onClick={flow(function* (evt) {
                    evt.preventDefault();

                    saving.set(true);
                    try {
                      yield saveCurrentlyEditingCourseAndCloseEditor({
                        userInfo,
                        cvEditContext,
                        queryClient,
                        courseSegmentStore,
                      });
                    } finally {
                      saving.set(false);
                    }

                    return false;
                  })}
                />
              )}
            </WSFormGrid>
          </CVEditCourseSegmentStyles>
        );
      }}
    />
  );
};

const CVEditCourseSegmentStyles = styled(WSFormGrid)``;

export interface SaveCurrentlyEditingCourseAndCloseEditorParams {
  userInfo: UserInfo;
  cvEditContext: CVEditContextStore;
  queryClient: QueryClient;
  courseSegmentStore: CVEditCourseSegmentStore;
}
export const saveCurrentlyEditingCourseAndCloseEditor = flow(function* ({
  userInfo,
  cvEditContext,
  queryClient,
  courseSegmentStore,
}: SaveCurrentlyEditingCourseAndCloseEditorParams) {
  const editorStore = courseSegmentStore.courseEditorStore;

  editorStore.hasAttemptedToClose = true;

  if (editorStore.validationErrors.length > 0) return;

  const cveCourse = editorStore.cveCourse;
  const candidateAPI = userInfo.fullAPI.candidateAPI;

  let result: BackendResponse;

  let description = editorStore.descriptionField.store.value;
  let courseTypeId = editorStore.courseTypeField.store.value;
  let organizer = editorStore.organizerField.store.value;
  let from = editorStore.fromDateField.store.value;
  let to = editorStore.toDateField.store.value;

  if (cveCourse.isNew) {
    result = yield candidateAPI.addCourseToCandidate({
      showSentryDialogOnError: true,
      userInfo,
      firmId: userInfo.idFirm,
      candidateId: userInfo.idCandidate,

      comment: description,
      from: from ? formatISO(from) : null,
      to: to ? formatISO(to) : null,
      courseType: courseTypeId,
      expirationDate: null,
      organizer: organizer,
      level: 0,

      //
      duplicateCheck: false,
      references: "",
    });
  } else {
    result = yield candidateAPI.editCourseForCandidate({
      showSentryDialogOnError: true,
      userInfo,
      firmId: userInfo.idFirm,
      candidateId: userInfo.idCandidate,

      courseId: Number(cveCourse.id),
      comment: description,
      from: from ? formatISO(from) : null,
      to: to ? formatISO(to) : null,
      courseType: courseTypeId,
      expirationDate: null,
      organizer: organizer,
      level: 0,

      //
      duplicateCheck: false,
      references: "",
    });
  }

  yield invalidateQueries(queryClient, ["candidateAPI.getCoursesForCandidate"]);

  if (result.ok) {
    editorStore.closeEditor();

    scrollIntoView(document.querySelector(".cve-container"));
  }
});
